import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FormikProps, useFormik } from "formik";
import { Checkbox, FormControlLabel } from "@mui/material";
import { DynamicFormProps } from "@/models/dynamic-form.model";
import * as Yup from "yup";
import { InputFieldType } from "@/constants/enums/input-field-type.enum";

const SimpleDynamicForm = memo<DynamicFormProps>(
  ({
    fields,
    initialValues = {},
    onSubmit,
    submitButtonText = "Save",
    readOnly = false,
  }) => {
    const [validationSchema, setValidationSchema] =
      useState<Yup.ObjectSchema<any> | null>(null);

    const formik = useFormik({
      initialValues,
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        await onSubmit(values);
      },
    });

    useEffect(() => {
      console.log("initialValues: ", initialValues);
      const validationSchemaData = Yup.object().shape(
        fields
          .filter(
            (field) =>
              field.type !== InputFieldType.BULLET_LIST &&
              field.type !== InputFieldType.NORMAL_TEXT,
          )
          .reduce((acc, field) => {
            console.log("field: ", field);

            return {
              ...acc,
              [field.name]:
                field.validation ||
                (field.type === InputFieldType.CHECKBOX
                  ? Yup.boolean().oneOf(
                      [true],
                      `${field.label} must be checked`,
                    )
                  : Yup.string().required(`${field.label} is required`)),
            };
          }, {}),
      );
      setValidationSchema(validationSchemaData);
      console.log("fields: ", fields);
    }, [fields]);

    useEffect(() => {
      fields
        .filter(
          (field) =>
            field.type !== InputFieldType.BULLET_LIST &&
            field.type !== InputFieldType.NORMAL_TEXT,
        )
        .forEach((field) => {
          formik.setFieldValue(field.name, initialValues[field.name]);
        });
    }, [validationSchema, initialValues]);

    return (
      <form onSubmit={formik.handleSubmit}>
        {fields.map((field, index) => (
          <div key={field.name}>
            {field.type === InputFieldType.CHECKBOX && (
              <div className="tw-mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formik.values[field.name])}
                      onChange={(event) => {
                        console.log("event: ", event);
                        formik.setFieldValue(field.name, event.target.checked);
                      }}
                      onBlur={formik.handleBlur}
                      name={field.name}
                      disabled={readOnly}
                      sx={{
                        color: "#E9EAEB",
                        "&.Mui-checked": { color: "#146C94" },
                        ".PrivateSwitchBase-input": {
                          display: "none",
                        },
                        "&.MuiCheckbox-root": {
                          paddingTop: "0px !important",
                          alignSelf: "start !important",
                        },
                      }}
                    />
                  }
                  label={field.label}
                />
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <div className="tw-text-red-500 tw-text-xs">
                    {formik.errors[field.name] as string}
                  </div>
                )}
              </div>
            )}

            {field.type === InputFieldType.TEXTAREA && (
              <div className="tw-mb-4">
                <label className="tw-block tw-text-[#232323] tw-text-sm tw-mb-1">
                  {field.label}
                </label>
                <textarea
                  name={field.name}
                  value={formik.values[field.name] || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={readOnly}
                  placeholder="Enter a description..."
                  className="tw-w-full tw-p-2 tw-border tw-rounded-md tw-text-sm"
                  rows={3}
                />
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <div className="tw-text-red-500 tw-text-xs">
                    {formik.errors[field.name] as string}
                  </div>
                )}
              </div>
            )}
            {field.type === InputFieldType.BULLET_LIST && (
              <div className="tw-text-[#232323] tw-text-sm">
                <ul className="tw-list-disc tw-pl-5">
                  <li className="tw-text-[#232323] tw-text-sm tw-mb-1">
                    {field.label}
                  </li>
                </ul>
              </div>
            )}
            {field.type === InputFieldType.NORMAL_TEXT && (
              <div className="tw-text-gray-500 tw-ml-4 tw-text-sm tw-mb-4">
                {field.label}
              </div>
            )}
          </div>
        ))}

        {fields.some(
          (field) =>
            field.type === InputFieldType.CHECKBOX ||
            field.type === InputFieldType.TEXTAREA,
        ) &&
          !readOnly && (
            <button
              type="submit"
              disabled={!formik.isValid || formik.isSubmitting}
              className={`tw-w-full tw-mt-4 tw-bg-[#146C94] tw-text-white tw-py-2 tw-px-4 tw-rounded-lg 
                ${
                  !formik.isValid || formik.isSubmitting
                    ? "tw-opacity-50 tw-cursor-not-allowed"
                    : "hover:tw-bg-[#105C84]"
                } 
                tw-transition-colors tw-duration-200`}
            >
              {formik.isSubmitting ? "Saving..." : submitButtonText}
            </button>
          )}
      </form>
    );
  },
);

SimpleDynamicForm.displayName = "SimpleDynamicForm";

export default SimpleDynamicForm;
